import {Injectable} from '@angular/core';
import {Observation, Project, Publication} from 'asax-common';
import {AsaxElasticService} from 'asax-elastic';
import {Observable, of, switchMap} from 'rxjs';
import {map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CellSimilarityService {

  constructor(private readonly asaxElasticService: AsaxElasticService) {
  }

  fetchSimilarProjectsForProject(projectCode: string): Observable<Project[]> {
    return this.asaxElasticService.searchByQuery({
      index: 'projects',
      body: {_source: ['title', 'proposalAbstract'], query: {term: {code: projectCode}}}
    }).pipe(
      map((data: any) => data.hits.hits.map(({_source}) => ({..._source}))),
      switchMap((projects: Project[]) => this.asaxElasticService.searchByQuery({
        index: 'projects',
        body: {
          _source: ['code', 'title', 'proposalAbstract'],
          size: 21,
          query: {
            more_like_this: {
              fields: ['titleText', 'proposalAbstractText'],
              like: [projects[0]?.title, projects[0]?.proposalAbstract]
            }
          }
        }
      })),
      map((data: any) => data.hits.hits.map(({_source}) => ({..._source}))),
    );
  }

  fetchSimilarPublicationsForPublication(bibCode: string): Observable<Publication[]> {
    return this.asaxElasticService.searchByQuery({
      index: 'publications',
      body: {_source: ['title', 'bibAbstract'], query: {term: {bibCode}}}
    }).pipe(
      map((data: any) => data.hits.hits.map(({_source}) => ({..._source}))),
      switchMap((publications: Publication[]) => this.asaxElasticService.searchByQuery({
        index: 'publications',
        body: {
          _source: ['bibCode', 'title', 'bibAbstract'],
          size: 21,
          query: {
            more_like_this: {
              fields: ['titleText', 'bibAbstractText'],
              like: [publications[0]?.title, publications[0]?.bibAbstract]
            }
          }
        }
      })),
      map((data: any) => data.hits.hits.map(({_source}) => ({..._source}))),
    );
  }

  fetchSimilarProjectsForObservation(datasetId: string): Observable<Project[]> {
    return this.asaxElasticService.searchByQuery({
      index: 'observations',
      body: {_source: ['project.title', 'project.proposalAbstract'], query: {term: {datasetId}}}
    }).pipe(
      map((data: any) => data.hits.hits.map(({_source}) => ({..._source}))),
      switchMap((observations: Observation[]) => {
        return this.asaxElasticService.searchByQuery({
          index: 'projects',
          body: {
            _source: ['code', 'title', 'proposalAbstract'],
            size: 21,
            query: {
              more_like_this: {
                fields: ['titleText', 'proposalAbstractText'],
                like: [observations[0]?.project?.title, observations[0]?.project?.proposalAbstract]
              }
            }
          }
        });
      }),
      map((data: any) => data.hits.hits.map(({_source}) => ({..._source}))),
    );
  }

  fetchSimilarPublicationsForObservation(datasetId: string): Observable<Publication[]> {
    return this.asaxElasticService.searchByQuery({
      index: 'observations',
      body: {_source: ['publications.title', 'publications.bibAbstract'], query: {term: {datasetId}}}
    }).pipe(
      map((data: any) => data.hits.hits.map(({_source}) => ({..._source}))),
      switchMap((observations: Observation[]) => {
        if (observations[0]?.publications?.length) {
          const like = [];
          observations[0]?.publications?.forEach(publication => like.push(publication.title));
          observations[0]?.publications?.forEach(publication => like.push(publication.bibAbstract));

          return this.asaxElasticService.searchByQuery({
            index: 'publications',
            body: {
              _source: ['bibCode', 'title', 'bibAbstract'],
              size: 20,
              query: {
                more_like_this: {
                  fields: ['titleText', 'bibAbstractText'],
                  like
                }
              }
            }
          });
        } else {
          return of({hits: {hits: []}});
        }
      }),
      map((data: any) => data.hits.hits.map(({_source}) => ({..._source}))),
    );
  }

  buildUrlFilteringByProjectCodes(projects: any[]): string {
    const baseUrl = this.buildBaseUrl();
    const projectCodes = projects?.map(project => project.code).join('|');
    const projectCodesEncoded = encodeURIComponent(projectCodes);

    return `${baseUrl}?result_view=projects&projectCode=${projectCodesEncoded}`;
  }

  buildUrlFilteringByBibCodes(publications: any[]): string {
    const baseUrl = this.buildBaseUrl();
    const bibCodes = publications?.map(publication => publication.bibCode).join('|');
    const bibCodesEncoded = encodeURIComponent(bibCodes);

    return `${baseUrl}?result_view=publications&bibCode=${bibCodesEncoded}`;
  }

  private buildBaseUrl(): string {
     const location = window.location;
     return `${location.origin}${location.pathname}`;
  }
}
